import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SignInLink } from "../components/shell/SignInLink";
import { AwakeningDemo } from "./signedOut/AwakeningDemo";
import { FeatureCards } from "./signedOut/FeatureCards";
import { GreetingScramble } from "./signedOut/GreetingScramble";
import { NeuralField } from "../components/shared/NeuralField";
import { StatsStrip } from "./signedOut/StatsStrip";
import { StreamingDemo } from "./signedOut/StreamingDemo";
import { useToolList } from "./signedOut/toolList";
import { usePublicProviders } from "./signedOut/usePublicProviders";
import styles from "./SignedOutView.module.css";


const prefersReducedMotion = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Whether any part of the element with `id` is on screen, so a link to it can step aside once it is. False until known, and where
 * `IntersectionObserver` is unavailable, so the link is offered rather than lost.
 *
 * @param id - The element's id.
 * @returns Whether it is in view.
 */
function useInView(id: string): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const element = document.getElementById(id);
    if (!element || typeof IntersectionObserver !== "function") {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    observer.observe(element);
    return () => observer.disconnect();
  }, [id]);

  return inView;
}

/**
 * What an unauthenticated visitor sees at the root of the shell: a live neural network behind a greeting in many languages, a pretend
 * model streaming answers while a meter counts its tokens and cost, and what everyday users can do here once signed in. Distinct from
 * both the unclassified (`UR-V-01`) and signed-in states, as the brief requires — there is no eligibility content before the user is
 * known. Everything that moves starts still for a viewer who prefers reduced motion, and one button pauses it all (WCAG 2.2.2).
 */
export function SignedOutView(): React.JSX.Element {
  const { t } = useTranslation();
  const [animate, setAnimate] = useState(() => !prefersReducedMotion());
  const providers = usePublicProviders();
  const tools = useToolList(providers);
  const featuresInView = useInView("signed-out-features");
  // The easter egg, each time the pointer draws the whole background network together: the network wakes ("awake"), the demo spends
  // its allowance and the network explodes, and the demo stays on show for a few seconds more ("aftermath") before everything resets.
  const [egg, setEgg] = useState<"idle" | "awake" | "aftermath">("idle");
  const [explosion, setExplosion] = useState(0);
  const wake = useCallback(() => setEgg("awake"), []);
  const explode = useCallback(() => {
    setEgg("aftermath");
    setExplosion((count) => count + 1);
  }, []);
  const reset = useCallback(() => setEgg("idle"), []);
  const awakening = animate && egg !== "idle";

  return (
    <div className={styles.page} data-page-width="wide">
      <section className={styles.hero} aria-labelledby="signed-out-title" data-animate={animate}>
        <div className={styles.aurora} aria-hidden="true" />
        <NeuralField animate={animate} awake={awakening && egg === "awake"} explosion={explosion} onConnected={animate && egg === "idle" ? wake : undefined} />

        <div className={styles.heroInner}>
          <div className={styles.copy}>
            <div className={styles.kicker}>{t("signedOut.kicker")}</div>
            <GreetingScramble animate={animate} className={styles.greeting} />
            <h1 id="signed-out-title" className={styles.title}>
              {t("signedOut.heroTitle")} <span className={styles.titleAccent}>{t("signedOut.heroTitleAccent")}</span>
            </h1>
            <p className={styles.subtitle}>{t("signedOut.description", { tools })}</p>

            <div className={styles.actions}>
              <SignInLink className={`button button-primary ${styles.cta}`}>{t("signedOut.signIn")}</SignInLink>
            {!featuresInView && (
              <a
                className={styles.scrollCue}
                href="#signed-out-features"
                onClick={(event) => {
                  event.preventDefault();
                  document.getElementById("signed-out-features")?.scrollIntoView({ behavior: animate ? "smooth" : "auto", block: "start" });
                }}
              >
                {t("signedOut.explore")}
                <span className={styles.scrollArrow} aria-hidden="true">
                  ↓
                </span>
              </a>
            )}
            </div>

            {providers && (
              <ul className={styles.tools} aria-label={t("signedOut.toolsLabel")}>
                {providers.map((provider) => (
                  <li key={provider.vendorId}>{provider.displayName}</li>
                ))}
              </ul>
            )}
          </div>

          {awakening ? <AwakeningDemo onLimit={explode} onEnd={reset} /> : <StreamingDemo animate={animate} tools={tools} />}
        </div>

        <div className={styles.heroFooter}>
          {animate && <p className={styles.hint}>{t("signedOut.hint")}</p>}
          <button type="button" className={styles.motionToggle} aria-pressed={!animate} onClick={() => {
              setAnimate((value) => !value);
              setEgg("idle");
            }}>
            {t(animate ? "signedOut.pauseAnimation" : "signedOut.playAnimation")}
          </button>
        </div>
      </section>

      <StatsStrip animate={animate} toolCount={providers?.length ?? null} />

      <section id="signed-out-features" className={styles.features} aria-labelledby="signed-out-features-title">
        <h2 id="signed-out-features-title" className={styles.featuresTitle}>
          {t("signedOut.featuresTitle")}
        </h2>
        <FeatureCards animate={animate} providers={providers} tools={tools} />
      </section>
    </div>
  );
}
